import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Heart,
  ShoppingBag,
  Sparkles,
  Check,
  Flame,
  Star,
} from "lucide-react";

import serumImg from "../assests/faceimg.webp"; // Path check kar lein

// Product Interface Type Definition
interface BestSellerProduct {
  id: string;
  rank: number;
  name: string;
  category: string;
  price: number;
  rating: number;
  reviews: number;
  image: string;
  tag: string;
  description: string;
}

const bestSellersData: BestSellerProduct[] = [
  {
    id: "glow-serum",
    rank: 1,
    name: "Glow Niacinamide Serum",
    category: "Serums & Oils",
    price: 48.0,
    rating: 4.9,
    reviews: 342,
    image: serumImg,
    tag: "#1 Most Loved",
    description: "Formulated with 10% Niacinamide and Zinc to target dark spots and boost instant glass-skin radiance.",
  },
  {
    id: "barrier-cream",
    rank: 2,
    name: "Ceramide Hydrating Barrier Cream",
    category: "Moisturizers",
    price: 52.0,
    rating: 4.9,
    reviews: 289,
    image: serumImg,
    tag: "#2 Viral Favorite",
    description: "Intensive moisture cream powered by 5 essential ceramides to restore compromised skin barriers.",
  },
  {
    id: "rice-toner",
    rank: 3,
    name: "Radiant Rice Essence Toner",
    category: "Toners & Cleansers",
    price: 36.0,
    rating: 4.8,
    reviews: 215,
    image: serumImg,
    tag: "#3 Top Rated",
    description: "77.78% pure rice extract essence for deep hydration, gentle brightening, and silky-smooth texture.",
  },
  {
    id: "peptide-night-oil",
    rank: 4,
    name: "Night Recovery Peptide Facial Oil",
    category: "Serums & Oils",
    price: 64.0,
    rating: 5.0,
    reviews: 180,
    image: serumImg,
    tag: "Luxury Pick",
    description: "Rejuvenating night elixir infused with copper peptides to visibly firm and plump skin overnight.",
  },
];

const categories: string[] = ["All", "Serums & Oils", "Moisturizers", "Toners & Cleansers"];

const BestSellers: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState<string>("All");
  const [wishlistIds, setWishlistIds] = useState<string[]>([]);
  const [addedCartId, setAddedCartId] = useState<string | null>(null);

  const toggleWishlist = (id: string): void => {
    setWishlistIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleAddToCart = (id: string): void => {
    setAddedCartId(id);
    setTimeout(() => setAddedCartId(null), 2000);
  };

  const filteredProducts: BestSellerProduct[] =
    activeCategory === "All"
      ? bestSellersData
      : bestSellersData.filter((item) => item.category === activeCategory);

  return (
    <div className="min-h-[calc(100vh-80px)] bg-[#FAF8F5] py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto space-y-10">
        
        {/* Header Section */}
        <div className="text-center space-y-3 py-6 border-b border-[#E8E2D9]">
          <span className="text-[11px] font-semibold uppercase tracking-[3px] text-[#9D7D4F] inline-flex items-center gap-1.5">
            <Flame size={14} className="text-[#9D7D4F]" /> Customer Favorites
          </span>
          <h1 className="font-serif font-bold text-4xl sm:text-5xl text-[#1F1B18] tracking-tight">
            Our Iconics & Best Sellers
          </h1>
          <p className="text-xs sm:text-sm text-gray-500 max-w-lg mx-auto leading-relaxed">
            Discover the most-wanted Seoul skincare formulations loved by thousands for proven, dermatologist-tested results.
          </p>
        </div>

        {/* Category Tabs */}
        <div className="flex items-center justify-center gap-2 sm:gap-4 overflow-x-auto pb-2">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`text-xs uppercase tracking-widest font-semibold px-5 py-2.5 rounded-full transition-all duration-300 ${
                activeCategory === cat
                  ? "bg-[#1F1B18] text-white shadow-sm"
                  : "bg-white text-gray-600 hover:text-black border border-[#E8E2D9]"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Best Sellers Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {filteredProducts.map((product) => (
            <div
              key={product.id}
              className="bg-white rounded-3xl border border-[#E8E2D9] overflow-hidden shadow-2xs hover:shadow-md transition-all duration-300 flex flex-col justify-between group relative"
            >
              {/* Product Image Area */}
              <div className="relative aspect-square bg-[#F7F4EF] overflow-hidden">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition duration-500"
                />

                {/* Rank Badge */}
                <span className="absolute top-3 left-3 bg-[#1F1B18] text-[#9D7D4F] text-[9px] font-bold uppercase tracking-wider px-3 py-1 rounded-full shadow-sm flex items-center gap-1">
                  <Sparkles size={10} /> {product.tag}
                </span>

                {/* Wishlist Button */}
                <button
                  onClick={() => toggleWishlist(product.id)}
                  className={`absolute top-3 right-3 p-2 bg-white/90 backdrop-blur-md rounded-full transition shadow-sm ${
                    wishlistIds.includes(product.id)
                      ? "text-red-500"
                      : "text-gray-400 hover:text-black"
                  }`}
                  aria-label="Add to Wishlist"
                >
                  <Heart
                    size={15}
                    fill={wishlistIds.includes(product.id) ? "currentColor" : "none"}
                  />
                </button>
              </div>

              {/* Product Info */}
              <div className="p-6 flex-1 flex flex-col justify-between space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] uppercase tracking-widest text-[#9D7D4F] font-semibold">
                      {product.category}
                    </span>
                    <div className="flex items-center gap-1 text-[11px] font-semibold text-[#1F1B18]">
                      <Star size={12} className="fill-[#9D7D4F] text-[#9D7D4F]" />
                      <span>{product.rating}</span>
                      <span className="text-gray-400 font-normal">({product.reviews})</span>
                    </div>
                  </div>

                  <Link
                    to={`/product/${product.id}`}
                    className="font-serif font-semibold text-lg text-[#1F1B18] hover:text-[#9D7D4F] transition line-clamp-1"
                  >
                    {product.name}
                  </Link>

                  <p className="text-xs text-gray-500 mt-2 line-clamp-2 leading-relaxed">
                    {product.description}
                  </p>
                </div>

                <div className="pt-2">
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-serif text-lg font-bold text-[#1F1B18]">
                      ${product.price.toFixed(2)}
                    </span>
                    <span className="text-[10px] text-emerald-600 font-semibold uppercase tracking-wider bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-100">
                      In High Demand
                    </span>
                  </div>

                  <button
                    onClick={() => handleAddToCart(product.id)}
                    className={`w-full py-3 rounded-xl text-xs uppercase tracking-widest font-semibold flex items-center justify-center gap-2 transition duration-300 ${
                      addedCartId === product.id
                        ? "bg-emerald-600 text-white"
                        : "bg-[#1F1B18] hover:bg-[#9D7D4F] text-white shadow-xs"
                    }`}
                  >
                    {addedCartId === product.id ? (
                      <>
                        <Check size={14} /> Added to Bag
                      </>
                    ) : (
                      <>
                        <ShoppingBag size={14} /> Add to Cart
                      </>
                    )}
                  </button>
                </div>
              </div>

            </div>
          ))}
        </div>

      </div>
    </div>
  );
};

export default BestSellers;